// Central content source for Banmore Electricals Pvt. Ltd. (BEPL)
// All photographs are watermarked + WebP-optimised at /public/wm (see /app/scripts/watermark.py).
const WM = `${process.env.PUBLIC_URL || ''}/wm`;

export const IMAGES = {
  heroHome: `${WM}/images_heroHome.webp`,
  cncLine: `${WM}/images_cncLine.webp`,
  builtUpCore: `${WM}/images_builtUpCore.webp`,
  qualityMain: `${WM}/images_qualityMain.webp`,
  defectPrevention: `${WM}/images_defectPrevention.webp`,
  exportMap: `${WM}/images_exportMap.webp`,
  infraHero: `${WM}/images_infraHero.webp`,
  heritage: `${WM}/images_heritage.webp`,
  aboutStory: `${WM}/about_story.webp`, // team at the Cut-to-Length 550mm CNC line
  qaLab: `${WM}/qa_lab.webp`, // Epstein tester / lab quality testing
  mfgProcess: `${WM}/mfg_process.webp`, // slitting & lamination shop floor
  aboutContactBanner: `${WM}/about_contact_banner_v3.webp`, // clean lamination storage warehouse
  heroSteel: `${WM}/images_heroSteel.webp`,
  factory: `${WM}/images_factory.webp`,
  quality: `${WM}/images_quality.webp`,
  coilBW: `${WM}/images_coilBW.webp`,
};

// Real Banmore factory photographs (uploaded by client)
export const REAL = {
  assembledCore: `${WM}/real_assembledCore.webp`, // green-framed clamped assembled cores
  laminations: `${WM}/real_laminations.webp`, // flat cut-to-size CRGO laminations
  builtCore: `${WM}/real_builtCore.webp`, // round/oval built-up core bundles
  inspection: `${WM}/real_inspection.webp`, // two workers inspecting core stacks
  builtCoreWide: `${WM}/real_builtCoreWide.webp`, // wide view of built-up core bundles
};

// CRGO Slit Coil photographs (uploaded by client)
export const SLIT = {
  packedStack: `${WM}/slit_packedStack.webp`,
  packedEye: `${WM}/slit_packedEye.webp`,
  slittingLine: `${WM}/slit_slittingLine.webp`,
  truckDispatch: `${WM}/slit_truckDispatch.webp`,
  burr: `${WM}/slit_burr.webp`,
  banner: `${WM}/slit_banner.webp`, // slitting machine banner
};

// CRGO Transformer Lamination photographs (uploaded by client)
export const LAM = {
  main: `${WM}/lam_main.webp`,
  closeUp: `${WM}/lam_closeUp.webp`,
  flat: `${WM}/lam_flat.webp`,
  packed: `${WM}/lam_packed.webp`,
};

// Additional shop-floor photos (uploaded by client)
export const FACPIX = {
  slittingWide: `${WM}/facpix_slittingWide.webp`,
  lamFloor: `${WM}/facpix_lamFloor.webp`,
  coreDispatch: `${WM}/facpix_coreDispatch.webp`,
  coreFramed: `${WM}/facpix_coreFramed.webp`,
};

export const FACILITY_GALLERY = [
  { src: SLIT.slittingLine, caption: 'Precision slitting line — up to 1250 mm width' },
  { src: REAL.assembledCore, caption: 'Assembled transformer cores on the shop floor' },
  { src: REAL.inspection, caption: 'Core inspection & measurement' },
  { src: SLIT.packedStack, caption: 'Banmore-packed CRGO slit coils' },
  { src: REAL.builtCore, caption: 'Built-up cores ready for dispatch' },
  { src: SLIT.truckDispatch, caption: 'Slit coils loaded for dispatch' },
  { src: LAM.main, caption: 'Cut-to-length CRGO laminations' },
  { src: LAM.closeUp, caption: 'Precision mitred lamination bundles' },
  { src: FACPIX.slittingWide, caption: 'Slitting line in operation' },
  { src: FACPIX.lamFloor, caption: 'Finished laminations on the shop floor' },
  { src: FACPIX.coreFramed, caption: 'Assembled transformer core — framed & clamped' },
  { src: FACPIX.coreDispatch, caption: 'Built-up core at the dispatch area' },
  { src: SLIT.burr, caption: 'Burr & thickness measurement' },
];

export const COMPANY = {
  name: 'Banmore Electricals Pvt. Ltd.',
  short: 'BEPL',
  founded: 1977,
  tagline: 'We make the heart of the transformer.',
  phone: '+91 9329212724',
  whatsapp: '919329212724',
  emailDisplay: 'export < @ > banmorecore.com',
  email2Display: 'banmoresteel < @ > gmail.com',
  factoryAddress: 'Plot 3, Industrial Area, Banmore, Dist. Morena – 474007, India',
  officeAddress: 'RTO Cross, Moti Mahal Road, Padav, Gwalior – 474007, India',
};

export const STATS = [
  { value: '50+', label: 'Years of Excellence', sub: 'Since 1977 · 3 generations' },
  { value: '6000', label: 'MT Annual Production', sub: '700 MT / month capacity' },
  { value: '16', label: 'Export Countries', sub: 'Across 5 continents' },
  { value: '30+', label: 'Transformer OEMs', sub: 'Globally trusted' },
];

export const NAV = [
  { label: 'Home', to: '/' },
  {
    label: 'Products',
    to: '/products',
    children: [
      { label: 'CRGO Transformer Laminations', to: '/products/crgo-transformer-laminations' },
      { label: 'CRGO Slit Coils', to: '/products/crgo-slit-coils' },
      { label: 'Built-up Transformer Core Assembly', to: '/products/built-up-transformer-core-assembly' },
      { label: 'CRGO Mother Coils', to: '/products/crgo-mother-coils' },
    ],
  },
  {
    label: 'Company',
    to: '/about',
    children: [
      { label: 'About Us', to: '/about' },
      { label: 'Infrastructure', to: '/infrastructure' },
      { label: 'Manufacturing Process', to: '/manufacturing-process' },
      { label: 'Quality Assurance', to: '/quality-assurance' },
      { label: 'Export Markets', to: '/export-markets' },
      { label: 'Career', to: '/career' },
    ],
  },
  { label: 'Contact', to: '/contact' },
];

export const PRODUCTS = [
  {
    slug: 'crgo-transformer-laminations',
    index: '01',
    name: 'CRGO Transformer Laminations',
    tagline: 'Cut-to-size precision laminations with guaranteed per-kg iron losses.',
    image: LAM.main,
    gallery: [LAM.main, LAM.closeUp, LAM.flat, LAM.packed, FACPIX.lamFloor],
    description:
      'Precision cut-to-length CRGO (Cold Rolled Grain Oriented) electrical steel laminations produced on fully automated CNC lines. Every lamination is burr-free, dimensionally accurate and grade-matched to your transformer design for guaranteed magnetic performance.',
    applications: [
      'Distribution transformers',
      'Power transformers up to 300 MVA',
      'Furnace & rectifier transformers',
      'Special & instrument transformers',
    ],
    advantages: [
      'Guaranteed per-kg iron losses',
      'Burr height under 20 microns',
      'Step-lap & mitred corner joints',
      'Full traceability from coil to lamination',
    ],
    capabilities: [
      { label: 'Steel Grades', value: '20KQ65, 20HP70, 20HP75, 23HP80D, 23HP85D / 90D / 95D / 100D, 27HP110–120' },
      { label: 'Thickness', value: '0.18 / 0.20 / 0.23 / 0.27 / 0.30 mm' },
      { label: 'Max Size', value: '300 MVA · max width 900 mm' },
      { label: 'Standards', value: 'IS 649 / 3024' },
    ],
  },
  {
    slug: 'crgo-slit-coils',
    index: '02',
    name: 'CRGO Slit Coils',
    tagline: 'State-of-the-art slitting up to 1250 mm width with mirror edges.',
    image: SLIT.packedStack,
    banner: SLIT.banner,
    gallery: [SLIT.banner, SLIT.packedStack, SLIT.slittingLine, FACPIX.slittingWide, SLIT.packedEye, SLIT.truckDispatch, SLIT.burr],
    description:
      'CRGO electrical steel slit to exact widths on high-speed precision slitting lines. Tight width tolerance, minimal edge burr and controlled camber ensure clean downstream core building and stacking.',
    applications: [
      'Wound core transformers',
      'Toroidal & C-cores',
      'Lamination feedstock',
      'Reactor & choke cores',
    ],
    advantages: [
      'Width tolerance ± 0.05 mm',
      'Slitting up to 1250 mm width',
      'Low edge burr, controlled camber',
      'Multiple coils per master coil',
    ],
    capabilities: [
      { label: 'Slitting Width', value: 'Up to 1250 mm' },
      { label: 'Thickness', value: '0.18 / 0.20 / 0.23 / 0.27 / 0.30 mm' },
      { label: 'Burr Level', value: '15 micron max' },
      { label: 'Coil ID / OD', value: '508 mm / up to 1200 mm' },
      { label: 'Lines', value: 'Multiple automated slitters' },
    ],
  },
  {
    slug: 'built-up-transformer-core-assembly',
    index: '03',
    name: 'Built-up Transformer Core Assembly',
    tagline: 'Ready-to-wind cores with guaranteed no-load losses — engineered to cut CRGO cost.',
    image: IMAGES.builtUpCore,
    gallery: [IMAGES.builtUpCore, REAL.assembledCore, REAL.builtCore, REAL.inspection],
    description:
      'Fully built-up and clamped transformer cores, engineered with 50 years of design know-how and delivered ready-to-wind with guaranteed no-load losses and no-load current. We advise on grade selection and core geometry to reduce CRGO consumption and improve transformer efficiency — saving OEMs assembly time and rejections.',
    applications: [
      'Distribution transformer OEMs',
      'Power transformer manufacturers up to 50 MVA',
      'High-efficiency (BEE star) & export-grade designs',
      'Rewinding, repair & renewable transformers',
    ],
    advantages: [
      'Guaranteed no-load loss & current',
      'Step-lap mitred construction, low noise',
      'Reduced CRGO cost with design consultancy',
      'Ready-to-wind delivery, high stacking factor',
    ],
    capabilities: [
      { label: 'Rating', value: 'Up to 50 MVA' },
      { label: 'Construction', value: '3 & 5 limb, step-lap' },
      { label: 'Stacking Factor', value: '≥ 0.97' },
      { label: 'Testing', value: 'Loss test on every core' },
    ],
  },
  {
    slug: 'crgo-mother-coils',
    index: '04',
    name: 'CRGO Mother Coils',
    tagline: 'Prime full-width CRGO master coils, stocked and ready for slitting.',
    image: IMAGES.heroSteel,
    gallery: [IMAGES.heroSteel, IMAGES.coilBW, SLIT.slittingLine],
    description:
      'Prime, full-width CRGO (Cold Rolled Grain Oriented) electrical steel master coils sourced from approved mills and stocked at our facility. Supplied as feedstock for slitting and lamination, every mother coil carries full mill traceability and grade certification for guaranteed magnetic performance.',
    applications: [
      'Feedstock for slitting & cut-to-length lines',
      'Transformer core manufacturers',
      'Lamination & wound-core producers',
      'Traders & OEM stockists',
    ],
    advantages: [
      'Prime, mill-certified CRGO grades',
      'Full mill-to-coil traceability',
      'Ready stock for fast turnaround',
      'Multiple grades & thicknesses available',
    ],
    capabilities: [
      { label: 'Steel Grades', value: '20KQ65, 20HP70, 20HP75, 23HP80D, 23HP85D / 90D / 95D / 100D, 27HP110–120' },
      { label: 'Thickness', value: '0.18 / 0.20 / 0.23 / 0.27 / 0.30 mm' },
      { label: 'Coil Width', value: 'Full width, up to 1250 mm' },
      { label: 'Standards', value: 'IS 649 / 3024' },
    ],
  },
];

export const WHY = [
  {
    title: 'Guaranteed Losses',
    body: 'Per-kg iron losses on laminations and no-load losses on assembled cores — contractually guaranteed.',
  },
  {
    title: 'Certified Compliance',
    body: 'First in India with BIS COC for CRGO transformer lamination. PGCIL & NTPC approved.',
  },
  {
    title: 'Engineering Solutions',
    body: '50 years of design expertise to help you reduce CRGO cost and improve transformer efficiency.',
  },
  {
    title: 'Worldwide Partnerships',
    body: 'Trusted by 30+ transformer manufacturing units, exporting to 16 countries across 5 continents.',
  },
];

export const CHAPTERS = [
  {
    no: '01',
    title: 'Heritage',
    body: 'Founded in 1977, Banmore Electricals has grown across three generations into a distinguished global manufacturer and exporter of CRGO transformer core lamination and built-core assembly.',
  },
  {
    no: '02',
    title: 'Precision',
    body: 'Superior CNC automated cut-to-length lines and state-of-the-art slitting lines deliver laminations and slit coils with micron-level accuracy and guaranteed magnetic performance.',
  },
  {
    no: '03',
    title: 'Quality',
    body: 'A dedicated CRGO testing laboratory and full material traceability — from incoming coil to finished core — keep every product compliant to IS 649 / 3024 and PGCIL / NTPC requirements.',
  },
  {
    no: '04',
    title: 'Global',
    body: 'A Government of India recognised Star Export House, supplying transformer OEMs across five continents with export-grade quality, documentation and on-time delivery.',
  },
];

export const CERTIFICATIONS = [
  { name: 'ISO 9001:2015', detail: 'Quality Management System' },
  { name: 'BIS COC', detail: 'First in India for CRGO lamination' },
  { name: 'PGCIL Approved', detail: 'Power Grid Corporation of India' },
  { name: 'NTPC Approved', detail: 'National Thermal Power Corp.' },
  { name: 'Star Export House', detail: 'Government of India' },
  { name: 'IS 649 / 3024', detail: 'Electrical steel compliance' },
];

export const INDUSTRIES = [
  'Power Transmission',
  'Distribution Utilities',
  'Renewable Energy',
  'Railways & Traction',
  'Industrial Furnaces',
  'Oil & Gas',
];

export const TESTIMONIALS = [
  {
    quote:
      'Consistent guaranteed losses and reliable delivery schedules have made Banmore a core partner for our distribution transformer line.',
    author: 'Procurement Head',
    org: 'Transformer OEM, Europe',
  },
  {
    quote:
      'Their engineering advice on core geometry directly reduced our CRGO cost without compromising efficiency.',
    author: 'Design Engineer',
    org: 'Power Transformer Manufacturer, India',
  },
  {
    quote:
      'Export documentation and packing were flawless. Material arrived exactly to specification.',
    author: 'Import Manager',
    org: 'Distributor, South America',
  },
];

export const NEWS = [
  {
    date: '2022',
    tag: 'Milestone',
    title: 'Star Export House recognition',
    body: 'Exporting to 16 countries across 5 continents, recognised as a Star Export House by the Government of India.',
  },
  {
    date: '2021',
    tag: 'Growth',
    title: 'USD 5 Million export milestone',
    body: 'Crossed USD 5 million in exports worldwide with record domestic sales and 6000 MT annual production.',
  },
  {
    date: '2017',
    tag: 'Capacity',
    title: 'Fully automated CNC expansion',
    body: 'Added CNC cut-to-length lines (550 mm) and slitting lines (1200 mm), lifting capacity to 700 MT / month.',
  },
];

export const TIMELINE = [
  { year: '1977', text: 'Founded as a manufacturer of transformers and CRGO lamination.' },
  { year: '1987', text: 'Executed World Bank IDA / IBRD deemed-export contracts.' },
  { year: '2000', text: 'Opened a dedicated unit for CRGO transformer lamination supply.' },
  { year: '2008', text: 'ISO 9001 certified with full coil-to-product traceability.' },
  { year: '2009', text: 'First export order successfully executed.' },
  { year: '2011', text: 'Became a BHEL vendor; bulk exports to Europe & South America.' },
  { year: '2017', text: 'Fully automated CNC & slitting expansion to 700 MT/month.' },
  { year: '2022', text: 'Star Export House — exporting to 16 countries, 5 continents.' },
  { year: '2025', text: 'Added 3 new CTL (cut-to-length) lines and 1 new slitting line.' },
  { year: '2026', text: 'Production capacity increased to 900 MT/month, with transformer lamination range up to 300 MVA.' },
];

export const PRODUCT_SEO = {
  'crgo-transformer-laminations': {
    title: 'CRGO Transformer Laminations Manufacturer | Banmore',
    desc: 'Cut-to-length CRGO transformer laminations in grades 20KQ65 to 27HP120 with guaranteed iron losses, step-lap mitred joints and IS 649/3024 compliance.',
    keywords: 'CRGO transformer laminations, cut to length laminations, CRGO lamination manufacturer India, transformer core lamination, step lap laminations',
  },
  'crgo-slit-coils': {
    title: 'CRGO Slit Coil Supplier & Manufacturer India | Banmore',
    desc: 'Precision CRGO slit coils up to 1250 mm wide with 15 micron max burr, 0.18 to 0.30 mm thickness and full traceability for transformer core manufacturers.',
    keywords: 'CRGO slit coil supplier, CRGO slitting services, grain oriented electrical steel, CRGO slit coils India, transformer steel slitting',
  },
  'built-up-transformer-core-assembly': {
    title: 'Built-up Transformer Core Assembly Manufacturer | Banmore',
    desc: 'Ready-to-wind built-up transformer core assemblies up to 300 MVA with guaranteed no-load losses, step-lap mitred construction and core design support.',
    keywords: 'built-up transformer core, assembled transformer cores, transformer core assembly, CRGO core manufacturer, power transformer core',
  },
  'crgo-mother-coils': {
    title: 'CRGO Mother Coils Supplier India | Banmore Electricals',
    desc: 'Prime CRGO mother coils (master coils) in M-grades and HiB, 0.18 to 0.30 mm, full width up to 1250 mm with mill traceability for slitting and lamination.',
    keywords: 'CRGO mother coils, CRGO master coils, CRGO electrical steel supplier, grain oriented steel coils India',
  },
};

export const EXPORT_REGIONS = [
  { region: 'Europe', countries: 5 },
  { region: 'Asia', countries: 4 },
  { region: 'Africa', countries: 3 },
  { region: 'South America', countries: 2 },
  { region: 'Middle East', countries: 2 },
];
